import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import { Trash2, ImageOff } from 'lucide-react';
import ArchivoService from '../../axios_services/archivos.service';
import './ModalImagenesGantt.css';

const ModalImagenesGantt = ({ isOpen, onClose, tarea, usuarioId }) => {
    const [imagenes, setImagenes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [previewLocal, setPreviewLocal] = useState(null);
    const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen && tarea?.id) {
            cargarImagenes();
        } else {
            setImagenes([]);
            setPreviewLocal(null);
            setArchivoSeleccionado(null);
        }
    }, [isOpen, tarea]);

    const cargarImagenes = async (silencioso = false) => {
        if (silencioso) {
            setIsRefreshing(true);
        } else {
            setIsLoading(true);
        }
        try {
            const data = await ArchivoService.getImagenesGantt(tarea.id);
            setImagenes(data || []);
        } catch {
            setImagenes([]);
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowed = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!allowed.includes(file.type)) {
            alert('Solo se permiten imágenes JPG o PNG.');
            e.target.value = '';
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('La imagen no puede superar 5 MB.');
            e.target.value = '';
            return;
        }

        setArchivoSeleccionado(file);
        setPreviewLocal(URL.createObjectURL(file));
    };

    const handleSubir = async () => {
        if (!archivoSeleccionado) return;
        setIsUploading(true);
        try {
            await ArchivoService.guardarImagenGantt(tarea.id, usuarioId, archivoSeleccionado);
            setPreviewLocal(null);
            setArchivoSeleccionado(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            await cargarImagenes(true);
        } catch {
            alert('Ocurrió un error al subir la imagen.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleCancelarSeleccion = () => {
        setPreviewLocal(null);
        setArchivoSeleccionado(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleEliminar = async (archivo_id) => {
        if (!window.confirm('¿Eliminar esta imagen?')) return;
        setIsLoading(true);
        try {
            await ArchivoService.eliminarImagenGantt(archivo_id);
            await cargarImagenes(true);
        } catch {
            alert('Ocurrió un error al eliminar la imagen.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal gantt-img-modal" backdrop={true}>
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>IMÁGENES — {tarea?.nombre?.toUpperCase() || ''}</h1>
                    <Button className="close-btn" onClick={onClose}>X</Button>
                </div>
            </ModalHeader>

            <div className="modal-body-footer">
                <ModalBody className="modal-body">
                    <div className="gantt-img-upload-zone">
                        <label className="gantt-img-upload-label" onClick={() => fileInputRef.current?.click()}>
                            Seleccionar imagen (JPG / PNG, máx. 5 MB)
                        </label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/jpg"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />

                        {previewLocal && (
                            <div className="gantt-img-preview-container">
                                <img
                                    src={previewLocal}
                                    alt="Vista previa"
                                    className="gantt-img-preview"
                                />
                                <p className="gantt-img-preview-name">{archivoSeleccionado?.name}</p>
                                <div className="gantt-img-preview-actions">
                                    <button
                                        className="gantt-btn-subir"
                                        onClick={handleSubir}
                                        disabled={isUploading}
                                    >
                                        {isUploading ? 'Subiendo...' : 'Confirmar y subir'}
                                    </button>
                                    <button
                                        className="gantt-btn-cancelar"
                                        onClick={handleCancelarSeleccion}
                                        disabled={isUploading}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="gantt-img-galeria-titulo">Imágenes adjuntas</div>

                    {isLoading ? (
                        <p className="gantt-img-empty">Cargando...</p>
                    ) : imagenes.length === 0 ? (
                        <div className="gantt-img-empty">
                            <ImageOff size={36} />
                            <p>No hay imágenes adjuntas.</p>
                        </div>
                    ) : (
                        <div className="gantt-img-grid">
                            {isRefreshing && <p className="gantt-img-refreshing">Actualizando...</p>}
                            {imagenes.map((img) => (
                                <div key={img.archivo_id} className="gantt-img-card">
                                    <a href={img.url_completa} target="_blank" rel="noreferrer">
                                        <img
                                            src={img.url_completa}
                                            alt="Imagen tarea"
                                            className="gantt-img-thumb"
                                        />
                                    </a>
                                    <button
                                        className="gantt-img-delete-btn"
                                        title="Eliminar"
                                        onClick={() => handleEliminar(img.archivo_id)}
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="button-container" style={{ marginTop: '16px' }}>
                        <Button className="gantt-btn-cerrar" onClick={onClose}>CERRAR</Button>
                    </div>

                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalImagenesGantt;
